import {
  computed,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useEventListener } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

// Inside an open conversation the side panel is one Inspector with two tabs, Details and Ask Polaris (the
// Copilot). It runs on the same two settings the old round switch buttons wrote, with no migration:
// open when either is on, on the Ask tab when `is_copilot_panel_open` is. The Ask tab follows
// `isCopilotPanelVisible` and Details follows `isDetailsPanelVisible`, so the rule of `useUISettings` (below
// `xl` a stored `is_copilot_panel_open` or `is_contact_sidebar_open` does not reopen the panel by itself)
// holds here too.
//
// How many conversation panels are mounted (the conversation views mount one while a conversation is
// open). While one is, the Copilot lives in its Ask tab and the global Copilot panel stays out of the way.
const mountedPanels = ref(0);
export const isConversationPanelMounted = computed(
  () => mountedPanels.value > 0
);

export function useMountConversationPanel() {
  onMounted(() => {
    mountedPanels.value += 1;
  });
  onBeforeUnmount(() => {
    mountedPanels.value -= 1;
  });
}

// The panel has no focus trap, so keyboard focus follows it by hand: opening it lands on the active tab,
// closing it returns to the header control that opens it (its own button, or More actions in the compact
// header). Both wait a render, since the target appears with the new state.
const focusAfterRender = selector =>
  nextTick(() => document.querySelector(selector)?.focus());
export const focusPanelTab = () =>
  focusAfterRender(
    '[data-conversation-panel] [role="tab"][data-state="active"]'
  );
export const focusPanelOpener = () =>
  focusAfterRender('[data-conversation-panel-opener]');

// Marks the top bar's "Ask Polaris": the two panels' click-outside ignore it (its own click toggles them, or the
// click outside would close and the same click reopen), and the global panel's Escape falls back to it.
export const ASK_OPENER_SELECTOR = '[data-ask-polaris-opener]';

// Below xl the conversation panel and the global Ask panel lie over the page without the Sheet (no focus trap:
// see ConversationSidebar.vue), so the Escape the Sheet would give is wired here. While the overlay is open,
// Escape closes it when the focus is inside it, nowhere (the body) or on the control that opened it, and the
// focus goes back to that control (or to `fallbackOpener` when that control is gone or never had the focus).
// Escape anywhere else belongs to what holds the focus: a dialog or menu opened from the panel (teleported
// out of it), the composer, the command bar. From xl up the panel is a column and nothing here runs.
// Dialogs, menus and lists that sit over the panel: native <dialog>, reka layers, the woot's modal mask and
// popover, and the woot layers with no role of their own, marked `data-overlay-layer` (EditContact's drawer, the
// multiselect's dropdown).
const LAYER_SELECTOR =
  'dialog[open], [role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-dismissable-layer], .modal-mask, [data-popover-content], [data-overlay-layer]';
const hasOpenLayerOver = panelElement =>
  [...document.querySelectorAll(LAYER_SELECTOR)].some(
    element =>
      element !== panelElement &&
      !element.contains(panelElement) &&
      element.getClientRects().length > 0
  );

export function useOverlayEscape({
  isOverlayOpen,
  panel,
  close,
  fallbackOpener,
}) {
  let opener = null;
  watch(
    isOverlayOpen,
    open => {
      opener = open ? document.activeElement : null;
    },
    { flush: 'sync' }
  );

  // A dialog or menu opened from the panel owns that Escape even when the focus stayed on the panel's button (the
  // woot's dialogs and menus do not always take the focus or mark the event). Whether one is open is read in the
  // capture phase, before its own handler closes it.
  let layerOpenAtKeydown = false;
  useEventListener(
    document,
    'keydown',
    event => {
      if (event.key !== 'Escape') return;
      layerOpenAtKeydown = hasOpenLayerOver(panel.value);
    },
    { capture: true }
  );

  useEventListener(document, 'keydown', event => {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing)
      return;
    if (!isOverlayOpen.value || layerOpenAtKeydown) return;
    const { target } = event;
    const panelElement = panel.value;
    const isInPanel = Boolean(panelElement?.contains(target));
    const isNowhere =
      target === document.body || target === document.documentElement;
    if (!isInPanel && !isNowhere && target !== opener) return;
    const returnTo =
      opener?.isConnected &&
      opener !== document.body &&
      !panelElement?.contains(opener)
        ? opener
        : null;
    close();
    nextTick(() =>
      (returnTo ?? document.querySelector(fallbackOpener))?.focus()
    );
  });
}

export function useConversationPanel() {
  const {
    updateUISettings,
    isCopilotPanelVisible,
    markCopilotPanelOpenedByUser,
    isDetailsPanelVisible,
    markDetailsPanelOpenedByUser,
  } = useUISettings();

  const currentAccountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );
  // Same gate the round Copilot button had.
  const hasAskTab = computed(() =>
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.POLARIS
    )
  );

  const isAskOpen = computed(
    () => hasAskTab.value && isCopilotPanelVisible.value
  );
  const isOpen = computed(() => isDetailsPanelVisible.value || isAskOpen.value);
  const activeTab = computed(() => (isAskOpen.value ? 'ask' : 'details'));

  const showDetails = () => {
    markDetailsPanelOpenedByUser();
    updateUISettings({
      is_contact_sidebar_open: true,
      is_copilot_panel_open: false,
    });
  };

  const showAsk = () => {
    markCopilotPanelOpenedByUser();
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: true,
    });
  };

  const setTab = tab => (tab === 'ask' ? showAsk() : showDetails());

  const close = () => {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  };

  // Alt+O, as the round switch had it: flips the Details side and always leaves the Copilot off.
  const toggleDetails = () => {
    // Flip what is on screen (a stored `true` held back below `xl` counts as closed).
    const willOpen = !isDetailsPanelVisible.value;
    if (willOpen) markDetailsPanelOpenedByUser();
    updateUISettings({
      is_contact_sidebar_open: willOpen,
      is_copilot_panel_open: false,
    });
  };

  return {
    hasAskTab,
    isOpen,
    isAskOpen,
    activeTab,
    showDetails,
    showAsk,
    setTab,
    close,
    toggleDetails,
  };
}
